import type {
  DriverScoreAPI,
  FleetScoreAPI,
  PeriodicalScoreAPI,
  ScoreForDriverAPI,
} from './APITypes';
import type {
  DriverScore,
  FleetScore,
  PeriodicalScore,
  ScoreForDriver,
} from './Types';
import { maybeDateTimeStrToMilli } from './utils';

import { dateTimeStrToMilli } from '../../formatters/dateTimeFormat';

const toPeriodicScore = (
  periodicalScoreAPI: PeriodicalScoreAPI
): PeriodicalScore => ({
  datePeriod: dateTimeStrToMilli(periodicalScoreAPI.date_period),
  score: periodicalScoreAPI.score,
  isAGap: periodicalScoreAPI.is_a_gap,
});

const toScoreForDriver = (
  scoreForDriver: ScoreForDriverAPI | {}
): ScoreForDriver | undefined => {
  if (Object.keys(scoreForDriver).length === 0) {
    return undefined;
  }
  const parsedScoreForDriver = scoreForDriver as ScoreForDriverAPI;

  return {
    driverId: parsedScoreForDriver.driver_id,
    startTimestamp: dateTimeStrToMilli(parsedScoreForDriver.start_timestamp),
    score: parsedScoreForDriver.score,
    distanceDriven: parsedScoreForDriver.distance_driven,
    eventRates: {
      fastAcceleration: parsedScoreForDriver.events_rates.fast_acceleration,
      hardBreak: parsedScoreForDriver.events_rates.hard_break,
      highSpeed: parsedScoreForDriver.events_rates.high_speed,
      speedyTurn: parsedScoreForDriver.events_rates.speedy_turn,
    },
  };
};

const toDriverScore = (driverScoreAPI: DriverScoreAPI): DriverScore => ({
  id: driverScoreAPI.id,
  active: driverScoreAPI.active,
  firstLicenseAcquirementDate: maybeDateTimeStrToMilli(
    driverScoreAPI.first_license_acquirement_date
  ),
  licenseExpirationDate: maybeDateTimeStrToMilli(
    driverScoreAPI.license_expiration_date
  ),
  licenseCategory: driverScoreAPI.license_category || undefined,
  name: driverScoreAPI.name,
  periodicalScores: {
    daily: driverScoreAPI.periodical_scores.daily.map(toPeriodicScore),
    weekly: driverScoreAPI.periodical_scores.weekly.map(toPeriodicScore),
    monthly: driverScoreAPI.periodical_scores.monthly.map(toPeriodicScore),
  },
  score: toScoreForDriver(driverScoreAPI.score),
});

export const toFleetScore = (responseAPI: FleetScoreAPI): FleetScore => ({
  id: responseAPI.id,
  name: responseAPI.name,
  active: responseAPI.active,
  periodicalScores: {
    daily: responseAPI.periodical_scores.daily.map(toPeriodicScore),
    weekly: responseAPI.periodical_scores.weekly.map(toPeriodicScore),
    monthly: responseAPI.periodical_scores.monthly.map(toPeriodicScore),
  },
  globalScore: {
    mean: responseAPI.global_score.mean,
    variance: responseAPI.global_score.variance,
    percentiles: responseAPI.global_score.percentiles,
  },
  drivers: responseAPI.drivers.map(toDriverScore),
  score: {
    average: responseAPI.score.average,
    deviation: {
      value: responseAPI.score.deviation.value,
      factor: responseAPI.score.deviation.factor,
    },
    eventRates: {
      fastAcceleration: responseAPI.score.events_rates.fast_acceleration,
      hardBreak: responseAPI.score.events_rates.hard_break,
      highSpeed: responseAPI.score.events_rates.high_speed,
      speedyTurn: responseAPI.score.events_rates.speedy_turn,
    },
  },
});

export default toFleetScore;
